// src/components/TurnstileWidget.jsx
// Renders a Cloudflare Turnstile challenge and reports the resulting token
// via onVerify. If VITE_TURNSTILE_SITE_KEY isn't set (local dev, or before
// Madembe provisions a site in the Cloudflare dashboard), this renders
// nothing and calls onVerify('') once so the parent form isn't blocked —
// matching the backend's fail-open-when-unconfigured posture in
// _verify_turnstile (main.py). Once a site key IS set, the backend enforces
// verification regardless of what the frontend sends, so there's no way to
// bypass this by disabling JS — the frontend widget is UX, not the boundary.
import { useEffect, useRef } from 'react'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

let scriptLoadPromise = null
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = SCRIPT_SRC
    el.async = true
    el.onload = resolve
    el.onerror = reject
    document.head.appendChild(el)
  })
  return scriptLoadPromise
}

export default function TurnstileWidget({ onVerify }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)

  useEffect(() => {
    if (!SITE_KEY) { onVerify(''); return }

    let cancelled = false
    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify(token),
        'expired-callback': () => onVerify(''),
        'error-callback': () => onVerify(''),
      })
    }).catch(() => onVerify(''))

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
      }
    }
  }, [onVerify])

  if (!SITE_KEY) return null
  return <div ref={containerRef} style={{ margin: '14px 0' }} />
}
